import { useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Minimize2, Maximize2, X, Bot } from "lucide-react";
import { ThemeProvider } from "styled-components";
import ChatBot from "react-simple-chatbot";
import { Box } from "@chakra-ui/react";
import "./ChatbotStyles.css";
import { DashboardContext } from "../../providers/DashboardProvider";
import HospitalProvider, { HospitalDashboardContext } from "../../providers/HospitalProvider";
import AsyncMessageGeneric from "./AsyncMessageGeneric";
import AsyncMessageTile from "./AsyncMessageTile";
import { dateFormat } from "../../utils/common.utils";
import { withProvider } from "../../hoc/withProvider";
import { userTypeKeysToEnum } from "../../utils/clientTypeUtils";

const theme = {
  background: "#f5f8fb",
  headerBgColor: "#007bff",
  headerFontColor: "white",
  headerFontSize: "18px",
  botBubbleColor: "#007bff",
  botFontColor: "white",
  userBubbleColor: "#f5f8fb",
  userFontColor: "#4a4a4a",
};

const tileDataMapping = [
  { value: "Revenue Cycle Temperature Gauge", label: "Revenue Cycle Temperature Gauge", trigger: "handleRevenueCycleTemperatureGauge" },
  { value: "RI Score", label: "RI Score", trigger: "handleRIScore" },
  { value: "E&M Distribution", label: "E&M Distribution", trigger: "handleRVUOpportunity" },
  { value: "Revenue Breakdown", label: "Revenue Breakdown", trigger: "handleRevenueBreakdown" },
  { value: "Outstanding Revenue Opportunity", label: "Outstanding Revenue Opportunity", trigger: "handleOutstandingRevenueOpportunity" },
  { value: "Total Charts", label: "Total Charts", trigger: "handleTotalCharts" },
  { value: "Potential Revenue Breakdown", label: "Potential Revenue Breakdown", trigger: "handlePotentialRevenueBreakdown" },
];

const improvementDataMapping = [
  { value: "Pinpoint my biggest sources of revenue leakage", label: "Pinpoint my biggest sources of revenue leakage", trigger: "handleMissingRevenue" },
  { value: "Show me my most frequently missed codes", label: "Show me my most frequently missed codes", trigger: "handleRevenueCodes" },
  { value: "Identify providers with the most coding gaps", label: "Identify providers with the most coding gaps", trigger: "handleProvidersWithGaps" },
];

const ChatbotComponent = () => {

  const dashboardContext = useContext(DashboardContext);
  const filter = dashboardContext.filterParams || {};
  const hospitalDashboardContext = useContext(HospitalDashboardContext);

  const [isOpen, setIsOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(true);
  const [isMinimized, setIsMinimized] = useState(true);
  const [showText, setShowText] = useState(true);
  const [isVisible, setIsVisible] = useState(showText);
  const [steps, setSteps] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => setShowText(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (showText) {
      setIsVisible(true);
    } else {
      setTimeout(() => setIsVisible(false), 500); // Delay unmount by 0.5s to allow fade-out animation
    }
  }, [showText]);

  const setCustomDates = (filterParams) => {
    const selectedDates = JSON.parse(window.localStorage.getItem("customDateFilter")) || {};
    Object.keys(selectedDates).forEach((prop) => {
      filterParams[prop] = dateFormat(new Date(selectedDates[prop]), "yyyy-MM-dd");
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const filterParams = {};
        if (filter?.label) {
          filterParams.filter = filter.label;
        } else {
          filterParams.filter = "all";
        }

        if (filter.label === "custom") {
          setCustomDates(filterParams);
          delete filterParams.filter;
        }

        let foundValidKey = false;
        // the filter that you are getting from dashboardContext already has this data properly added as {health_system_id: integer}
        for (const key in userTypeKeysToEnum) {
          if (key in filter) {
            filterParams[key] = filter[key];
            foundValidKey = true;
            break;
          }
        }

        if (foundValidKey) {
          await hospitalDashboardContext.getOutstandingRevenue(filterParams);
        } 
        
      } catch (error) {
        // Todo: Error handling
      }
    };

    fetchData();
  }, [filter]);


  useEffect(() => {
    setSteps(generateSteps());
  }, []);

  const generateSteps = () => [
    {
      id: "1",
      message: "Hi there! I’ve curated key insights from RevIntegrity that might interest you. Would you like to explore them now?",
      trigger: "initialChoice",
    },
    {
      id: "initialChoice",
      options: [
        { value: "yes", label: "Yes", trigger: "mainMenu" },
        { value: "notNow", label: "Not now", trigger: "catchUpLater" },
      ],
    },
    {
      id: "catchUpLater",
      message: "No problem! Let's catch up later whenever you're ready.",
      trigger: () => {
        setTimeout(() => {
          setIsHidden(true);
        }, 2500);
        return "continueOption";
      }
    },
    {
      id: "continueOption",
      options: [
        { value: "continue", label: "Continue", trigger: "mainMenu" },
      ],
    },
    {
      id: "mainMenu",
      message: "Great! Choose an option below to dive into detailed insights or improvement opportunities:",
      trigger: "menuOptions",
    },
    {
      id: "menuOptions",
      options: [
        { value: "specificTiles", label: "Insights on Specific Tiles", trigger: "pickTile" },
        { value: "improvements", label: "Key Areas for Improvement", trigger: "improvementOptions" },
      ],
    },
    {
      id: "pickTile",
      message: "Select a specific tile to get in-depth insights tailored to your data.",
      trigger: "tileSelection",
    },
    {
      id: "tileSelection",
      options: [
        ...tileDataMapping.map(option => ({
          value: option.value,
          label: option.label,
          trigger: `confirmation_${option.value}`,
        })),
        { value: "back", label: "< Back", trigger: "afterOptionsMessage" },
      ],
    },
    ...tileDataMapping.map(option => ({
      id: `confirmation_${option.value}`,
      message: () => {
        const confirmations = [
          "Got it! Gathering insights for you now...",
          "Just a moment while I fetch your insights...",
          "Hold on, I'm compiling the details...",
        ];
        return confirmations[Math.floor(Math.random() * confirmations.length)];
      },
      trigger: option.trigger,
    })),

    ...tileDataMapping.map(option => ({
      id: option.trigger,
      component: <AsyncMessageTile option={option.value} />,
      waitAction: true,
      trigger: "responseMessage",
    })),
    {
      id: "responseMessage",
      message: "Would you like insights on any other tiles?",
      trigger: "tileSelection",
    },
    {
      id: "improvementOptions",
      message: "Select an area to explore opportunities for improvement:",
      trigger: "improvementSelection",
    },
    {
      id: "improvementSelection",
      options: [
        ...improvementDataMapping.map(option => ({
          value: option.value,
          label: option.label,
          trigger: `confirmation_${option.value}`,
        })),
        { value: "back", label: "< Back", trigger: "afterOptionsMessage" },
      ],
    },
    ...improvementDataMapping.map(option => ({
      id: `confirmation_${option.value}`,
      message: () => {
        const confirmations = [
          "Got it! Gathering insights for you now...",
          "Just a moment while I fetch your insights...",
          "Hold on, I'm compiling the details...",
        ];
        return confirmations[Math.floor(Math.random() * confirmations.length)];
      },
      trigger: option.trigger,
    })),

    ...improvementDataMapping.map(option => ({
      id: option.trigger,
      component: <AsyncMessageGeneric option={option.value} />,
      waitAction: true,
      trigger: "impResponseMessage",
    })),
    {
      id: "impResponseMessage",
      message: "Select any other areas to explore opportunities for improvement..",
      trigger: "improvementSelection",
    },
    {
      id: "afterOptionsMessage",
      message: "Hope the information was helpful! Would you like to explore other insights?",
      trigger: "afterOptionsOptions",
    },
    {
      id: "afterOptionsOptions",
      options: [
        { value: "continue", label: "Continue", trigger: "mainMenu" },
        { value: "notNow", label: "Not now", trigger: "endCatchUpLater" },
      ],
    },
    {
      id: "endCatchUpLater",
      message: "Got it! Feel free to return anytime if you need more insights.",
      trigger: () => {
        setTimeout(() => {
          setIsHidden(true);
        }, 2500);
        return "continueOption";
      }
    },
  ];

  return (
    <div className="chatbot-wrapper">
      <div
        className="chatbot-float-container"
        onClick={() => {
          setIsOpen(true);
          setIsHidden(!isHidden);
        }}
      >
        {(isVisible && !isOpen) && (
          <div className={`custom-tooltip ${showText ? "" : "hide"}`}>
            AI Suggestion
            <div className="tooltip-arrow"></div>
          </div>
        )}
        <motion.div
          className="chatbot-icon-wrapper"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          animate={{ opacity: 1 }}
        >
          <Bot size={40} className="chatbot-icon" />
        </motion.div>
      </div>

      {isOpen && (
        <motion.div
          className={`chatbot-container`}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          style={{ display: isHidden ? "none" : "block" }}
        >
          <div className="chatbot-header">
            <span className="title">AI Suggestion</span>
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              <button
                className="chatbot-btn-icon"
                onClick={() => setIsMinimized(!isMinimized)}
              >
                {isMinimized ? <Maximize2 size={18} /> : <Minimize2 size={18} />}
              </button>
              <button
                className="chatbot-btn-icon close-btn"
                onClick={() => setIsHidden(true)}
              >
                <X size={18} />
              </button>
            </Box>
          </div>
          <ThemeProvider theme={theme}>
            <ChatBot
              steps={steps}
              floating={false}
              headerTitle=""
              width={isMinimized ? "350px" : "510px"}
              enableSmoothScroll={true}
            />
          </ThemeProvider>
        </motion.div>
      )}
    </div>
  );
};

export default withProvider(HospitalProvider, ChatbotComponent);
